/**
 * @file: inspectionSiteGridUtil.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */

import { getDefectiveLevel } from '@euler/functions/getDefectiveLevel';
import { InventoryQuery } from '@euler/functions/useInventory';
import { DefectiveLevel, InspectionTemplateConf } from '@euler/model';
import { CustomIssue, SiteInspection } from '@euler/model/task-detail';
import { SiteGirdItemTag } from '@euler/typings/styled';
import { comparer, isNotNull } from '@euler/utils';
import { array2map, uniq } from '@euler/utils/array';
import { dateFromValue } from '@euler/utils/datetime';

export const kDefaultHardwareCapableGroupId = '__hardware__';
export const kCustomIssueGroupId = '__custom_issues__';
export const kDefaultHardwareGroupName = '硬件项目';
export const kCustomIssueGroupName = '自定义部位';

export type ItemInfo = {
  itemType: 'site' | 'custom-issue';
  isHardwareCapable: boolean;
  groupId: string;
  siteId: number;
  code: string;
  customIssueId?: number;
  name: string;
  icon?: string;
  iconType?: 'image' | 'svg';
  defectiveLevel?: DefectiveLevel;
  tag?: SiteGirdItemTag;
  timestamp: number;
  required: boolean;
  mediaCaptureRequired: boolean;
};

export type GroupSectionDataItem = { groupId: string; items: ItemInfo[] };

export type GroupSection = {
  title: string;
  groupId: string;
  index: number;
  data: [GroupSectionDataItem];
  hiddenSites?: ItemInfo[];
};

export function siteGridItemMatches(
  item: { code: string; name: string },
  keyword: string | undefined,
) {
  if (!keyword) return true;
  return (
    item.code.toLowerCase().includes(keyword.toLowerCase()) ||
    item.name.toLowerCase().includes(keyword.toLowerCase())
  );
}

export function buildGroupSectionsFromTemplate(
  flowId: string | undefined,
  inventoryQuery: InventoryQuery,
  template: {
    conf: InspectionTemplateConf;
    isHardwareInspectionEnabled?: boolean;
  },
  inspectedSites?: SiteInspection[],
  customIssues?: CustomIssue[],
  keyword?: string,
): GroupSection[] {
  const groups = template.conf.categories.flatMap(x => x.groups);
  const inspectedSiteMap = array2map(inspectedSites ?? [], x => x.siteId);

  const mapSite = (
    groupId: string,
    required: (siteId: number) => boolean,
    mediaCaptureRequired: (siteId: number) => boolean,
    siteId: number,
  ) => {
    const site = inventoryQuery.getSiteById(siteId);
    if (!site || !siteGridItemMatches(site, keyword)) return null;
    const inspectedSite = inspectedSiteMap.get(siteId);
    const item: ItemInfo = {
      itemType: 'site',
      isHardwareCapable: site.supportsIdevice,
      groupId,
      siteId,
      code: site.code,
      name: site.name,
      icon: site.iconUrl ?? undefined,
      iconType: site.iconUrl
        ? site.iconUrl.includes('.svg')
          ? 'svg'
          : 'image'
        : undefined,
      defectiveLevel: inspectedSite
        ? getDefectiveLevel(
            inspectedSite.abnormalLevel!,
            inspectedSite.severityLevel!,
          )
        : undefined,
      tag:
        site.supportsIdevice && template.isHardwareInspectionEnabled
          ? 'hardware-capable'
          : required(siteId)
          ? 'required'
          : undefined,
      timestamp: 0,
      required: required(siteId),
      mediaCaptureRequired: mediaCaptureRequired(siteId),
    };
    return item;
  };

  const siteIdSetInTemplate = new Set<number>();

  let sections = groups.map(group => {
    const disabledSiteIdSet = new Set(group.disabledSiteIds ?? []);
    const defaultHiddenSiteIdSet = new Set(group.defaultHiddenSiteIds ?? []);
    const requiredSiteIdSet = new Set(group.requiredSiteIds ?? []);
    const mediaCaptureRequiredSiteIds = new Set(
      group.mediaCaptureRequiredSiteIds ?? [],
    );

    // filter out disabled sites and the sites that should be hidden by default.
    const targetSiteIds: Set<number> = new Set(
      group.siteIds.filter(
        x =>
          !disabledSiteIdSet.has(x) &&
          (keyword || !defaultHiddenSiteIdSet.has(x)),
      ),
    );

    const expectedNumberOfSites = targetSiteIds.size;

    for (const defaultHiddenSiteId of group.defaultHiddenSiteIds ?? []) {
      // however, if the default hidden site has inspetion results and
      // it is not disabled, move it out to the visible grid area.
      // it's also moved out in case the expected number of sites is zero.
      if (
        (expectedNumberOfSites === 0 ||
          inspectedSiteMap.has(defaultHiddenSiteId)) &&
        !targetSiteIds.has(defaultHiddenSiteId) &&
        !disabledSiteIdSet.has(defaultHiddenSiteId)
      ) {
        targetSiteIds.add(defaultHiddenSiteId);
      }
    }

    const mapFn = mapSite.bind(
      null,
      group.id,
      x => requiredSiteIdSet.has(x),
      x => mediaCaptureRequiredSiteIds.has(x),
    );
    const items = [...targetSiteIds].map(mapFn).filter(isNotNull);

    const hiddenSiteIds = keyword
      ? []
      : (group.defaultHiddenSiteIds ?? []).filter(x => !targetSiteIds.has(x));

    const section: GroupSection = {
      index: 0,
      title: group.name,
      groupId: group.id,
      data: [{ groupId: group.id, items }],
      hiddenSites: hiddenSiteIds.map(mapFn).filter(isNotNull),
    };

    for (const item of items) {
      siteIdSetInTemplate.add(item.siteId);
    }

    if (section.hiddenSites != null) {
      for (const hiddenSite of section.hiddenSites) {
        siteIdSetInTemplate.add(hiddenSite.siteId);
      }
    }

    return section;
  });

  // the list of hardware capable items which are intended to be
  // always displayed as the first section at the top.
  const hardwareCapableItems = template.isHardwareInspectionEnabled
    ? uniq(
        sections.flatMap(x => x.data[0].items.filter(t => t.isHardwareCapable)),
        x => x.siteId,
      ).map(item => ({ ...item, groupId: kDefaultHardwareCapableGroupId }))
    : [];

  if (hardwareCapableItems.length) {
    sections.unshift({
      index: 0,
      groupId: kDefaultHardwareCapableGroupId,
      title: kDefaultHardwareGroupName,
      data: [
        {
          groupId: kDefaultHardwareCapableGroupId,
          items: hardwareCapableItems,
        },
      ],
      hiddenSites: [],
    });
  }

  const nonTemplateInspectedSites = (inspectedSites ?? []).filter(
    x =>
      !siteIdSetInTemplate.has(x.siteId) &&
      (x.flowId == null || x.flowId === flowId),
  );

  if (customIssues?.length || nonTemplateInspectedSites.length > 0) {
    const items: ItemInfo[] = [];
    sections.push({
      index: 0,
      groupId: kCustomIssueGroupId,
      title: kCustomIssueGroupName,
      data: [
        {
          groupId: kCustomIssueGroupId,
          items,
        },
      ],
    });
    if (customIssues?.length) {
      items.push(
        ...customIssues
          .filter(
            x =>
              (!x.siteId || !siteIdSetInTemplate.has(x.siteId)) &&
              (!x.flowId || x.flowId === flowId),
          )
          .map<ItemInfo>(customIssue => ({
            itemType: 'custom-issue',
            isHardwareCapable: false,
            groupId: kCustomIssueGroupId,
            siteId: 0,
            code: '',
            customIssueId: customIssue.id,
            name: customIssue.siteName,
            defectiveLevel: getDefectiveLevel(
              customIssue.abnormalLevel,
              customIssue.severityLevel,
            ),
            timestamp: dateFromValue(customIssue.createdAt).getTime(),
            required: false,
            mediaCaptureRequired: false,
          })),
      );
    }
    if (nonTemplateInspectedSites.length) {
      for (const inspectedSite of nonTemplateInspectedSites) {
        const site = inventoryQuery.getSiteById(inspectedSite.siteId);
        if (site) {
          if (!siteGridItemMatches(site, keyword)) continue;

          items.push({
            itemType: 'site',
            groupId: kCustomIssueGroupId,
            isHardwareCapable: site.supportsIdevice,
            siteId: site.id,
            code: site.code,
            name: site.name,
            icon: site.iconUrl ?? undefined,
            iconType: site.iconUrl
              ? site.iconUrl.includes('.svg')
                ? 'svg'
                : 'image'
              : undefined,
            defectiveLevel: inspectedSite
              ? getDefectiveLevel(
                  inspectedSite.abnormalLevel!,
                  inspectedSite.severityLevel!,
                )
              : undefined,
            tag: site.supportsIdevice ? 'hardware-capable' : undefined,
            timestamp: dateFromValue(inspectedSite.timestamp).getTime(),
            required: false,
            mediaCaptureRequired: false,
          });
        } else {
          for (const customIssue of inspectedSite.customIssues) {
            if (
              !siteGridItemMatches(
                { code: '', name: customIssue.siteName },
                keyword,
              )
            ) {
              continue;
            }
            items.push({
              itemType: 'custom-issue',
              isHardwareCapable: false,
              groupId: kCustomIssueGroupId,
              siteId: 0,
              code: '',
              customIssueId: customIssue.id,
              name: customIssue.siteName,
              defectiveLevel: getDefectiveLevel(
                customIssue.abnormalLevel,
                customIssue.severityLevel,
              ),
              timestamp: dateFromValue(customIssue.createdAt).getTime(),
              required: false,
              mediaCaptureRequired: false,
            });
          }
        }
      }
    }
    items.sort((x, y) => comparer(x.timestamp, y.timestamp));
  }

  // don't show section w/o any items.
  sections = sections.filter(x => x.data[0].items.length > 0);

  // assign index to each section.
  sections.forEach((x, index) => (x.index = index));

  return sections;
}
